import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { AssetTypePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { usePinFilters } from "@/lib/pins-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

const categoryLabels: Record<string, string> = {
  barrier: "Speed Barrier",
  dugout: "Dugout",
  pavilion: "Pavilion",
  jersey: "Jersey",
  other: "Other",
};

export default function AssetTypesPage() {
  const [selected, setSelected] = useState<AssetTypePublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const { activeOrgIds } = usePinFilters();
  const hasPinFilter = activeOrgIds.length > 0;
  const pinKey = activeOrgIds.join(",");

  async function handleDelete(id: string) {
    if (!confirm("Delete this asset type? This action cannot be undone.")) return;
    try {
      await api.deleteAssetType(id);
      if (selected?.id === id) setSelected(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete asset type");
    }
  }

  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: AssetTypePublic[]; total: number }> => {
      if (hasPinFilter) {
        const res = await api.getAssetTypes();
        const scoped = res.asset_types.filter((t) => activeOrgIds.includes(t.organization_id));
        return applyClientListParams(scoped, params, {
          searchFields: (t) => [t.name, t.category],
          sortAccessors: { name: (t) => t.name, category: (t) => t.category },
          defaultSort: "name",
        });
      }
      const res = await api.getAssetTypes({
        page: params.page, per_page: params.per_page,
        search: params.search, sort: params.sort, order: params.order,
      });
      return { data: res.asset_types, total: res.total ?? res.asset_types.length };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [pinKey],
  );

  const columns: DataTableColumn<AssetTypePublic>[] = [
    {
      key: "name", header: "Name", sortable: true, className: "font-medium",
      render: (t) => <Link to={`/asset-types/${t.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{t.name}</Link>,
    },
    { key: "category", header: "Category", sortable: true, render: (t) => categoryLabels[t.category] ?? t.category },
    { key: "size", header: "Size (mm)", className: "text-muted-foreground", render: (t) => `${t.width_mm} x ${t.height_mm}` },
    { key: "ad_spaces", header: "Ad Spaces", className: "text-muted-foreground", render: (t) => t.ad_space_count },
    { key: "assets", header: "Assets", className: "text-muted-foreground", render: (t) => t.asset_count },
    {
      key: "actions", header: "", headerClassName: "w-20",
      render: (t) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {t._actions.can_edit && (
            <Link to={`/asset-types/${t.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })}>
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {t._actions.can_delete && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="asset-types-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Asset Types</h1>
            <p className="text-muted-foreground">Manage advertising asset type templates</p>
          </div>
          <div className="flex gap-2">
            <Link to="/asset-types/new" className={buttonVariants({ size: "sm" })} data-action="create">
              <Plus className="mr-2 h-4 w-4" />
              New Asset Type
            </Link>
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        <PinFilterBar entityType="Organization" />

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={`${pinKey}:${refreshTick}`}
            rowKey={(t) => t.id}
            rowProps={(t) => ({ "data-item": "asset-type", "data-item-id": t.id })}
            rowClassName={(t) => (selected?.id === t.id ? "bg-muted" : undefined)}
            searchPlaceholder="Search asset types..."
            defaultSort="name"
            emptyMessage="No asset types yet."
            onRowClick={(t) => setSelected(t)}
            onStateChange={setTableState}
          />
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Asset Type">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Category">{categoryLabels[selected.category] ?? selected.category}</PreviewField>
            <PreviewField label="Size">{selected.width_mm} x {selected.height_mm} mm</PreviewField>
            <PreviewField label="Ad Spaces">{selected.ad_space_count}</PreviewField>
            <PreviewField label="Assets">{selected.asset_count}</PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
